import {
    Card,
    CardContent,
    Box,
    Typography,
    Chip,
    IconButton,
    Divider,
    Autocomplete,
    Tooltip,
    Select,
    Option,
    Badge,
} from "@mui/joy";
import { ArrowDownFromLine, ArrowUpFromLine, Minus, MouseOff, Plus, Trash2, CircleAlert } from "lucide-react";
import { motion } from "framer-motion";

interface ProductCardProps {
    product: any;
    selectedValues: any;
    quantityBidding: any;
    handleValueChange: (value: any, name: string) => void;
    handleColorChange?: (category: string, color: string | null, manufacturerProductId?: string) => void;
    expandIcon?: boolean;
    onExpandClick?: (clickedItem: string) => any;
    isExpanded?: boolean;
    handleQuantityUpdate: (name: string, event: "add" | "remove", additionalItem?: boolean) => void;
    onRemove?: (productCategory: string) => void;
    priceRanges?: Record<string, { min: number; max: number }>;
    productIdentifier?: string; // For ProductCardSimple to identify the product
}

function getMeasurementUnits(product: any): string {
    if (!product?.MeasurementType) return "";
    return product.MeasurementType.map((mes: any) => mes.Unit).join(", or ");
}

function getSelectedProductName(selectedValues: any, productName: string): string | null {
    const rawValue = selectedValues[productName]?.value as string | undefined;
    return rawValue ?? null;
}

function getProductOptions(product: any): string[] {
    // Get unique product names (group by name, ignoring color variations)
    const subcategories = product.Subcategories || [];
    const uniqueNames = Array.from(new Set<string>(subcategories.map((e: any) => e.Name)));
    return uniqueNames;
}

function getSafeSelectedProduct(options: string[], selected: string | null): string | null {
    if (!selected) return null;
    return options.includes(selected) ? selected : null;
}

function getColorOptionsForSelection(product: any, selectedProductName: string | null): string[] {
    if (!selectedProductName) return [];
    const subcategories = product.Subcategories || [];
    const matchingSubs = subcategories.filter((sub: any) => sub.Name === selectedProductName && sub.Color);
    if (matchingSubs.length === 0) return [];
    const uniqueColors = Array.from(new Set<string>(matchingSubs.map((sub: any) => sub.Color as string))) as string[];
    return uniqueColors;
}

function getInitialColor(
    product: any,
    selectedValues: any,
    colorOptions: string[],
    productName: string
): string | null {
    const currentColor = selectedValues[productName]?.color as string | undefined;
    const selectedManufacturerProductId = selectedValues[productName]?.manufacturerProductId as string | undefined;

    if (currentColor && colorOptions.includes(currentColor)) {
        return currentColor;
    }

    if (selectedManufacturerProductId) {
        const subcategories = product.Subcategories || [];
        const matchingSubs = subcategories.filter(
            (sub: any) => sub.Name === selectedValues[productName]?.value && sub.Color
        );
        const matchedById = matchingSubs.find((sub: any) => sub.Id === selectedManufacturerProductId && sub.Color);
        if (matchedById?.Color && colorOptions.includes(matchedById.Color as string)) {
            return matchedById.Color as string;
        }
    }

    return null;
}

function computePriceDisplay(
    product: any,
    selectedValues: any,
    priceRanges?: Record<string, { min: number; max: number }>
): string {
    const selectedValue = selectedValues[product.Name] as any;
    const productValue = selectedValue?.value;

    if (selectedValue?.unitPrice && selectedValue.unitPrice > 0) {
        return `$${selectedValue.unitPrice.toFixed(2)}`;
    }

    if (productValue && priceRanges && priceRanges[productValue]) {
        const range = priceRanges[productValue];
        return `$${range.min.toFixed(2)} - $${range.max.toFixed(2)}`;
    }

    if (priceRanges && product.Subcategories) {
        const allRanges = product.Subcategories.map((sub: any) => priceRanges[sub.Name]).filter(
            (range: any) => range !== undefined
        ) as Array<{ min: number; max: number }>;

        if (allRanges.length > 0) {
            const minPrice = Math.min(...allRanges.map((r) => r.min));
            const maxPrice = Math.max(...allRanges.map((r) => r.max));
            return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
        }
    }

    return "-";
}

function computePriceLabel(
    product: any,
    selectedValues: any,
    priceRanges?: Record<string, { min: number; max: number }>
): string {
    const selectedValue = selectedValues[product.Name] as any;

    if (selectedValue?.unitPrice && selectedValue.unitPrice > 0) {
        return "Unit Price";
    }

    if (selectedValue?.value && priceRanges && priceRanges[selectedValue.value]) {
        return "Unit Price Range";
    }

    if (priceRanges && product.Subcategories) {
        const hasRanges = product.Subcategories.some((sub: any) => priceRanges[sub.Name]);
        if (hasRanges) {
            return "Price Range";
        }
    }

    return "Price";
}

function QuantityControl(props: {
    productName: string;
    selectedValues: any;
    onChange: (event: "add" | "remove") => void;
}) {
    const { productName, selectedValues, onChange } = props;
    const quantity = selectedValues[productName]?.quantity ?? "-";

    return (
        <Box
            sx={{
                display: "flex",
                borderRadius: "40px",
                p: 0.2,
                pb: 0.15,
                alignItems: "center",
                gap: 1,
                border: "1px solid",
                borderColor: "divider",
            }}
        >
            <IconButton
                variant="soft"
                size="sm"
                color="danger"
                onClick={() => onChange("remove")}
                sx={{
                    flex: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    "--IconButton-size": "32px",
                    borderRadius: "50%",
                }}
            >
                <Minus size={12} />
            </IconButton>

            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0 }} component={"span"}>
                {quantity}
            </Typography>
            <Badge badgeContent={quantity} variant="soft" color="warning">
                <IconButton
                    variant="soft"
                    color="success"
                    size="sm"
                    onClick={() => onChange("add")}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        "--IconButton-size": "32px",
                        borderRadius: "50%",
                    }}
                >
                    <Plus size={12} />
                </IconButton>
            </Badge>
        </Box>
    );
}

function ProductSelectionSection(props: {
    product: any;
    selectedValues: any;
    handleValueChange: (value: any, name: string) => void;
    handleColorChange?: (category: string, color: string | null, manufacturerProductId?: string) => void;
}) {
    const { product, selectedValues, handleValueChange, handleColorChange } = props;

    const options = getProductOptions(product);
    const rawSelectedName = getSelectedProductName(selectedValues, product.Name);
    const safeSelectedName = getSafeSelectedProduct(options, rawSelectedName);

    const selectedProductName = safeSelectedName;
    const colorOptions = getColorOptionsForSelection(product, selectedProductName);

    const showColorSelector = handleColorChange && selectedProductName && colorOptions.length > 0;
    const initialColor = showColorSelector
        ? getInitialColor(product, selectedValues, colorOptions, product.Name)
        : null;

    return (
        <Box display="flex" flexDirection="column" gap={0.5}>
            <Autocomplete
                value={safeSelectedName}
                variant="outlined"
                sx={{ maxWidth: "300px" }}
                placeholder="Select"
                options={options}
                onChange={(_event: any, value: any) => handleValueChange(value, product.Name)}
                popupIcon={null}
                clearIcon={null}
                slotProps={{
                    root: {
                        sx: {
                            boxShadow: "none",
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            py: 0.5,
                            minHeight: "auto",
                            fontSize: "inherit",
                            "&:hover": { background: "transparent" },
                            "&::before": { display: "none" },
                            "&::after": { display: "none" },
                        },
                    },
                    input: {
                        sx: {
                            fontWeight: 500,
                        },
                    },
                }}
            />

            {showColorSelector && (
                <Select
                    size="sm"
                    placeholder="Select color"
                    value={initialColor ?? undefined}
                    onChange={(_event, value) => {
                        const color = value as string | null;
                        const subcategories = product.Subcategories || [];
                        const matchingSubs = subcategories.filter(
                            (sub: any) => sub.Name === selectedProductName && sub.Color
                        );
                        const matched = matchingSubs.find((sub: any) => sub.Color === color);
                        const manufacturerProductId = matched?.Id as string | undefined;
                        if (handleColorChange) {
                            handleColorChange(product.Name, color, manufacturerProductId);
                        }
                    }}
                    sx={{ maxWidth: 240, mt: 0.25 }}
                >
                    {colorOptions.map((colorOption) => (
                        <Option key={colorOption as string} value={colorOption as string}>
                            {colorOption as string}
                        </Option>
                    ))}
                </Select>
            )}
        </Box>
    );
}

function ProductCard(props: ProductCardProps) {
    const {
        product,
        selectedValues,
        handleValueChange,
        handleColorChange,
        handleQuantityUpdate,
        onRemove,
        priceRanges,
    } = props;

    function handleQuantityChange(event: "add" | "remove") {
        handleQuantityUpdate(product.Name, event);
    }

    const selection = selectedValues[product.Name];
    const hasNoValue = !selection?.value;
    const hasZeroQuantity = !selection?.quantity || selection.quantity === 0;
    const hasIssue = hasNoValue || hasZeroQuantity;

    return (
        <Card
            sx={{
                p: 1.5,
                px: 2,
                borderRadius: 10,
                border: "1px solid",
                borderColor: "divider",
                zIndex: 2,
            }}
            variant="soft"
        >
            <CardContent>
                <Box display="flex" justifyContent="space-between" gap={3}>
                    <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                                {product.Name}
                            </Typography>
                            <Typography level="body-xs">({getMeasurementUnits(product)})</Typography>
                            {hasIssue && (
                                <Tooltip
                                    title={hasNoValue ? "Selection Required" : "Quantity is Zero"}
                                    arrow
                                    variant="solid"
                                    color="warning"
                                >
                                    <motion.div
                                        animate={{ opacity: [1, 0.5, 1], scale: [1, 1.1, 1] }}
                                        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                        style={{ display: "flex", alignItems: "center" }}
                                    >
                                        <CircleAlert size={18} color="#ed6c02" />
                                    </motion.div>
                                </Tooltip>
                            )}
                            <Box>
                                {product?.DependentsOn?.map((dependsOn: any) => (
                                    <Chip
                                        key={dependsOn}
                                        size="sm"
                                        sx={{
                                            borderRadius: 6,
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                        color={selectedValues[dependsOn] ? "success" : "danger"}
                                    >
                                        {dependsOn}
                                    </Chip>
                                ))}
                            </Box>
                        </Box>

                        <ProductSelectionSection
                            product={product}
                            selectedValues={selectedValues}
                            handleValueChange={handleValueChange}
                            handleColorChange={handleColorChange}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <QuantityControl
                            productName={product.Name}
                            selectedValues={selectedValues}
                            onChange={handleQuantityChange}
                        />
                    </Box>

                    <Divider orientation="vertical" />

                    <Box display="flex" flexDirection="column" justifyContent="center" minWidth={140}>
                        <Typography sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            {computePriceDisplay(product, selectedValues, priceRanges)}
                        </Typography>
                        <Typography level="body-xs">
                            {computePriceLabel(product, selectedValues, priceRanges)}
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    <Box display="flex" alignItems="center" gap={3}>
                        <IconButton
                            size="sm"
                            color="danger"
                            variant="soft"
                            onClick={() => onRemove?.(product.Name)}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 6,
                                "--IconButton-size": "26px",
                            }}
                        >
                            <Trash2 size={12} />
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

function ProductCardSimple(props: ProductCardProps) {
    const { product, handleQuantityUpdate, onRemove, priceRanges, productIdentifier } = props;

    function handleQuantityChange(event: "add" | "remove") {
        handleQuantityUpdate(product, event, true);
    }

    const quantity = product?.quantity ?? 0;
    const hasZeroQuantity = !quantity || quantity <= 0;

    const hasUnitPrice = product?.unitPrice && product.unitPrice > 0;
    const hasRange = product?.name && priceRanges?.[product.name];

    const priceText = hasUnitPrice
        ? `$${product.unitPrice.toFixed(2)}`
        : hasRange
        ? `$${priceRanges![product.name].min.toFixed(2)} - $${priceRanges![product.name].max.toFixed(2)}`
        : "-";

    return (
        <Card
            sx={{
                p: 1.5,
                px: 2,
                borderRadius: 10,
                border: "1px solid",
                borderColor: hasZeroQuantity ? "warning.300" : "divider",
            }}
            variant="soft"
        >
            <CardContent>
                <Box display="flex" justifyContent="space-between" gap={3}>
                    <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography level="body-xs" sx={{ fontWeight: 500 }}>
                                {product.product}
                            </Typography>
                            {hasZeroQuantity && (
                                <Tooltip title="Quantity is Zero" arrow variant="solid" color="warning">
                                    <motion.div
                                        animate={{ opacity: [1, 0.5, 1], scale: [1, 1.1, 1] }}
                                        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                        style={{ display: "flex", alignItems: "center" }}
                                    >
                                        <CircleAlert size={18} color="#ed6c02" />
                                    </motion.div>
                                </Tooltip>
                            )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                {product.name}
                            </Typography>
                        </Box>
                    </Box>

                    <Box>
                        <QuantityControl
                            productName={product.name}
                            selectedValues={{ [product.name]: { quantity } }}
                            onChange={handleQuantityChange}
                        />
                    </Box>

                    <Divider orientation="vertical" />

                    <Box display="flex" flexDirection="column" justifyContent="center" minWidth={140}>
                        <Typography sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{priceText}</Typography>
                        <Typography level="body-xs">{hasUnitPrice ? "Unit Price" : "Unit Price Range"}</Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    <Box display="flex" alignItems="center" gap={3}>
                        <IconButton
                            size="sm"
                            color="danger"
                            variant="soft"
                            onClick={() => productIdentifier && onRemove?.(productIdentifier)}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 6,
                                "--IconButton-size": "26px",
                            }}
                        >
                            <Trash2 size={12} />
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export { ProductCard, ProductCardSimple };
